import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { apiFetch } from '../apiClient';

// How often the unread count is refreshed while the app is open. Long on purpose: nothing here is
// time-critical, and this is one COUNT per user per minute against a shared database.
const POLL_MS = 60000;
const SHOW_READ_KEY = 'psops.notifications.showRead';

function timeAgo(value) {
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [unread, setUnread] = React.useState(0);
  const [error, setError] = React.useState('');
  // Read items are hidden by default so the panel is a to-do list, not a log. The choice sticks
  // per browser rather than per session — a preference this small isn't worth a server round trip.
  const [showRead, setShowRead] = React.useState(() => {
    try { return localStorage.getItem(SHOW_READ_KEY) === '1'; } catch { return false; }
  });

  const toggleShowRead = () => {
    setShowRead((prev) => {
      try { localStorage.setItem(SHOW_READ_KEY, prev ? '0' : '1'); } catch { /* private mode */ }
      return !prev;
    });
  };

  // The count polls; the feed itself is only fetched when the panel is opened.
  React.useEffect(() => {
    let cancelled = false;
    const tick = () => {
      // A background tab has nobody to show a badge to, and each poll renews the access token —
      // which is what made an abandoned tab look like an all-night string of logins.
      if (document.visibilityState === 'hidden') return;
      apiFetch('/notifications/unread-count')
        .then((r) => { if (!cancelled) setUnread(r.unreadCount); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    // Catch up immediately on return, so the badge isn't stale for up to a minute.
    document.addEventListener('visibilitychange', tick);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);

  const openPanel = async () => {
    setOpen(true);
    setError('');
    try {
      const { items: feed, unreadCount } = await apiFetch('/notifications');
      setItems(feed);
      setUnread(unreadCount);
    } catch (err) {
      setError(err.message);
    }
  };

  const activate = async (item) => {
    setOpen(false);
    if (!item.readAt) {
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n)));
      setUnread((n) => Math.max(0, n - 1));
      apiFetch(`/notifications/${item.id}/read`, { method: 'PATCH' }).catch(() => {});
    }
    // Where a notification takes you. Suggestions have their own filterable/sortable screen now
    // (SuggestionReviews.js) rather than the submit-only popup, so a reply notification lands you
    // on the actual answer instead of an empty submit form; the approvals queue is a panel inside
    // My Time, opened by query param.
    // ?id= opens that suggestion's detail on arrival (SuggestionReviews.js).
    if (item.entityType === 'suggestion') navigate(`/suggestions${item.entityId ? `?id=${item.entityId}` : ''}`);
    if (item.entityType === 'timesheet') navigate('/my-time?approvals=1');
  };

  const markAllRead = async () => {
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    setUnread(0);
    apiFetch('/notifications/read-all', { method: 'POST' }).catch(() => {});
  };

  const visible = showRead ? items : items.filter((n) => !n.readAt);
  const hiddenCount = items.length - visible.length;

  return (
    <div className="notif-wrap">
      <button
        className="header-help-btn notif-btn"
        onClick={() => (open ? setOpen(false) : openPanel())}
        title="Notifications"
        aria-label={unread ? `Notifications (${unread} unread)` : 'Notifications'}
      >
        <Bell size={17} strokeWidth={1.75} aria-hidden />
        {unread > 0 && <span className="notif-count">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <>
          {/* Click-away layer, so the panel closes like a menu rather than needing the button again. */}
          <div className="notif-backdrop" onClick={() => setOpen(false)} />
          <div className="notif-panel" role="dialog" aria-label="Notifications">
            <div className="notif-head">
              <span>Notifications</span>
              <span className="notif-head-actions">
                {(showRead || hiddenCount > 0) && (
                  <button className="notif-mark-all" onClick={toggleShowRead}>
                    {showRead ? 'Hide read' : `Show read (${hiddenCount})`}
                  </button>
                )}
                {unread > 0 && (
                  <button className="notif-mark-all" onClick={markAllRead}>Mark all read</button>
                )}
              </span>
            </div>
            {error && <div className="notif-empty">{error}</div>}
            {!error && visible.length === 0 && (
              <div className="notif-empty">{items.length === 0 ? 'Nothing yet.' : 'Nothing unread.'}</div>
            )}
            {visible.map((item) => (
              <button
                key={item.id}
                className={`notif-item ${item.readAt ? '' : 'unread'}`}
                onClick={() => activate(item)}
              >
                <span className="notif-title">{item.title}</span>
                {item.body && <span className="notif-body">{item.body}</span>}
                <span className="notif-time">{timeAgo(item.createdAt)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
